import {
    lazyCreateNewFluidComponent,
    lazyGetAgendaCodeDetails,
    FluidContainerConfigForDiv,
} from 'owa-fluid-integrations';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import { RecipientContainer } from 'owa-link-data';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import {
    setIsCollabSpacePresent,
    setIsNewDoc,
    setIsCollabAllowed,
    resetCollabSpaceStore,
    setFluidLinkFailed,
} from '../mutators/collabSpaceMutators';
import { defaultCollabSpaceName } from '../components/CollabSpace.locstring.json';
import loc from 'owa-localize';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { lazyInitializeAttachmentFilePicker } from 'owa-attachment-filepicker';
import { ProviderLoadStateOption, getProviderLoadState } from 'owa-fileprovider-store';
import { addAttendeeFromEmailAddress } from 'owa-calendar-compose-form-actions';
import {
    isSeriesMaster,
    isFTDFSeriesMaster,
    isSeriesInstance,
} from 'owa-calendar-event-capabilities';
import { logUsage } from 'owa-analytics';
import { hasAccessIssue } from './collabSpaceErrorCheck';
import { isEventFromDifferentMailbox } from '../utils/isEventFromDifferentMailbox';
import { lazyCreatePerfDatapoint } from 'owa-fluid-perf-datapoints';
import { getCollabSpaceControlNode } from '../components/CollabSpaceRenderer';
import { getCollabSpaceActionMenuItems } from './getCollabSpaceActionMenuItems';
import { initializeStore } from '../mutators/initializeStore';
import { createCollabSpaceViewStateId } from './createCollabSpaceViewStateId';
import { collabSpaceContainerId } from './constants';
import { getViewStateById } from '../selectors/collabSpaceSelectors';
import { MeetingInformation } from '@fluidx/office-fluid-types';

export default function createFluidCollabSpace(
    event: CalendarEvent,
    fluidOwaSource: FluidOwaSource,
    viewStateId: string | null
) {
    const fullComposeViewState = getFullComposeViewState();

    if (
        !isFluidEnabledForSource(fluidOwaSource) ||
        !fullComposeViewState ||
        isEventFromDifferentMailbox(event) ||
        hasAccessIssue()
    ) {
        return;
    }

    lazyCreatePerfDatapoint.importAndExecute(fluidOwaSource);

    if (!viewStateId) {
        viewStateId = createCollabSpaceViewStateId();
    } else {
        initializeStore(viewStateId);
    }

    const recipientContainers: RecipientContainer[] = [
        event.RequiredAttendees,
        event.OptionalAttendees,
    ];
    const composeParams = {
        getRecipientWells: () => recipientContainers,
        fromAddress: event.ParentFolderId?.mailboxInfo?.mailboxSmtpAddress,
        linkIdContainer: fullComposeViewState.attachmentWell,
    };

    setIsNewDoc(true, viewStateId);
    setIsCollabSpacePresent(true, viewStateId);

    const createSpace = () => {
        lazyCreateNewFluidComponent.import().then(createNewFluidComponent => {
            let collabSpaceFileName = loc(defaultCollabSpaceName);
            if (event.Subject) {
                collabSpaceFileName = event.Subject;
            } else {
                logUsage('AddCollabSpaceWithDefaultTitle', undefined, { isCore: true });
            }
            setIsCollabAllowed(true, viewStateId);
            lazyGetAgendaCodeDetails.import().then(getCodeDetails => {
                const codeDetails = getCodeDetails();
                const containerId = `${collabSpaceContainerId}#${viewStateId}`;
                const containerNode = document.getElementById(containerId) as HTMLDivElement;
                if (!containerNode) {
                    resetCollabSpaceStore(viewStateId);
                    setFluidLinkFailed(true, viewStateId);
                    return;
                }

                const controlNode = getCollabSpaceControlNode(event, viewStateId);
                const customMenuItems = getCollabSpaceActionMenuItems(
                    event,
                    fluidOwaSource,
                    viewStateId
                );

                const onMountNewCollabSpace = (loadedInfo: any) => {
                    onMount(loadedInfo, event, viewStateId, true /* isNewCollabSpace */);
                };

                const containerConfig: FluidContainerConfigForDiv = {
                    configType: 'div',
                    containerDiv: containerNode,
                    controlNode,
                    customMenuItems,
                    onMount: onMountNewCollabSpace,
                };

                createNewFluidComponent(
                    containerConfig,
                    codeDetails,
                    undefined, // heroComponentConfiguration
                    codeDetails.config.blueprintId, // componentType
                    composeParams,
                    collabSpaceFileName,
                    window, // Update to pass in a target window if calendar adds support for projection popout
                    FluidOwaSource.CalendarCompose,
                    addAttendeeFromEmailAddress,
                    viewStateId
                ).then(() => {
                    if (isSeriesInstance(event)) {
                        logUsage('AddCollabSpaceForOccurence', undefined, { isCore: true });
                    }
                });
            });
        });
    };

    if (getProviderLoadState() === ProviderLoadStateOption.Loaded) {
        createSpace();
    } else {
        lazyInitializeAttachmentFilePicker.import().then(initializeAttachmentFilePicker => {
            initializeAttachmentFilePicker(createSpace);
        });
    }
}

export const onMount = async (
    loadedInfo: any, // loadedInfo is typed as 'any' since any references to office-web-host should only be in owa-fluid-integrations
    event: CalendarEvent,
    viewStateId: string,
    isNewCollabSpace: boolean
) => {
    const meetingInformationProvider = (await loadedInfo.component)
        .ComponentMeetingInformationProvider;
    if (!meetingInformationProvider) {
        return;
    }

    const meetingInformation: MeetingInformation = {};

    if (!event.UID) {
        // If no UID is present on the event it means this is a new event draft in which case we only want to set the
        // isShared property
        const isSeriesMasterDraft = !!event.Recurrence?.RecurrencePattern;
        meetingInformation.isShared = isSeriesMasterDraft || isSeriesMaster(event);
        meetingInformationProvider.setMeetingInformation(meetingInformation);
        return;
    }

    // This indicates we're trying to create/edit/view a series in which case we want to set the seriesId
    if (isSeriesMaster(event) || isFTDFSeriesMaster(event) || isSeriesInstance(event)) {
        meetingInformation.seriesId = event.SeriesId;

        // This determines whether a user is trying to create a collab space in a series instance
        const isNewInstanceCollabSpace = isNewCollabSpace && isSeriesInstance(event);

        const viewState = getViewStateById(viewStateId);

        if (!isNewInstanceCollabSpace && viewState?.isShared === 'true') {
            meetingInformation.isShared = true;
            meetingInformationProvider.setMeetingInformation(meetingInformation);
            return;
        }
    }

    meetingInformation.meetingId = event.UID;
    meetingInformation.isShared = false;
    meetingInformationProvider.setMeetingInformation(meetingInformation);
};
